<template>
  <ABaseModal ref="modal" v-bind="$attrs">
    <div class="modal">
      <div class="modal-header">
        <span class="btn-close" @click="close()"
          ><b>창닫기</b
          ><img src="@assets/img/contents/icon_modal-close.svg" alt=""
        /></span>
        <slot name="modal-header" />
      </div>
      <div class="modal-body">
        <slot name="modal-body" />
      </div>
      <div class="modal-captcha">
        <slot name="modal-captcha" />
      </div>
      <div class="modal-sub-footer">
        <slot name="modal-sub-footer" />
      </div>
      <div class="modal-footer">
        <template v-if="$slots['modal-footer']">
          <slot name="modal-footer" />
        </template>
        <template v-else>
          <button @click="close()" class="modal-footer-button close">
            <img src="@assets/img/pop-close.svg" alt="닫기버튼" />
          </button>
        </template>
      </div>
    </div>
  </ABaseModal>
</template>
<script setup lang="ts">
import { ref, watch } from 'vue';
import { ABaseModal } from '@atoms';
import { useScrollLock } from '@composables';
import { useRouter } from 'vue-router';

const { lockScroll, unLockScroll } = useScrollLock();
defineProps({
  title: {
    type: String,
    default: 'Modal Title',
  },
  subTitle: {
    type: String,
    default: 'Modal Subtitle',
  },
});

const modal = ref();
const message = ref('');
const { currentRoute } = useRouter();
const emits = defineEmits(['complete', 'close']);

let isClosing = false; // 중복 호출 방지 플래그

const open = (msg: string) => {
  lockScroll();
  modal.value.open();
  message.value = msg;
};

const close = ({ complete = false, args = null } = {}) => {
  if (isClosing) return;
  isClosing = true;

  unLockScroll();
  if (complete) {
    emits('complete', args);
  }
  modal.value.close();
  emits('close', true);
  // 일정 시간 후 플래그를 초기화
  setTimeout(() => {
    isClosing = false;
  }, 100);
};

watch(currentRoute, () => {
  close();
});

defineExpose({
  open,
  close,
});
</script>

<style lang="scss" scoped>
.modal {
  top: 50%;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 45px;
  width: 617px;
  z-index: 1000;
  overflow: hidden;
  padding-bottom: 0;
  @apply mobile:wi-[320];
  &-header {
    display: flex;
    flex-direction: column;
    //font-family: 'GmarketSans', sans-serif;
  }
  &-title {
    @apply text-lg font-bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.45;
    letter-spacing: -0.5px;
  }
  &-subtitle {
    @apply text-base;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: -0.4px;
    text-align: left;
  }
  &-body {
    @apply py-6;
  }
  &-footer {
    @apply flex justify-between gap-10;
    &-button {
      @apply w-full text-base;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.75;
      letter-spacing: -0.8px;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      align-items: center;
      &.close {
        @apply text-white rounded-full wi-[48] he-[48] mx-auto mt-10;
        background: rgba(255, 255, 255, 0.11);
        width: 56px;
        height: 56px;
      }
      &.complete {
        @apply text-white;
        background: var(--btn-color);
        padding: 7px 0 9px;
      }
    }
  }
  /* 스크롤바 커스텀 */
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background: #b6b6b6; /* 스크롤바 색상 */
    border-radius: 7.5px; /* 스크롤바 라운드 테두리 */
    height: 5%;
  }
  ::-webkit-scrollbar-track {
    background: #f3f3f3; /*스크롤바 뒷 배경 색상*/
    border-radius: 7.5px;
  }
}
.modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 20px;
  border-top: 20px solid var(--main-color);
  z-index: 1;
}
.modal::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 20px;
  border-bottom: 20px solid var(--main-color);
  z-index: 1;
}

.btn-close {
  display: flex;
  align-items: center;
  gap: 0 5px;
  width: 100%;
  justify-content: flex-end;
  cursor: pointer;
  padding: 40px 35px 0 0;
  b {
    font-weight: 500;
    font-size: 14px;
    color: #847f7f;
  }
  img {
    width: 13px;
    height: 13px;
  }
}

@media (min-width: 320px) and (max-width: 1279px) {
  .modal {
    width: 92%;
    padding: 0 13px;
  }
  .modal-footer-button.close {
    width: 48px;
    height: 48px;
  }
  .modal-footer-button.close img {
    width: 16px;
    height: 16px;
  }

  .btn-close {
    padding: 40px 0 0 0;
    b {
      font-size: 0.75rem;
    }
  }
}
</style>
